<template>
  <v-container v-resize="onResize" class="product__container pa-0" fluid>
    <ContentMenu
      v-if="showContentMenu"
      class="product-content-menu"
      :class="{ hidden: !showContentMenu }"
      :activated-menu="activatedMenu"
      :items="contentMenu"
    />

    <Breadcrumbs v-if="!isUsingGift" :items="breadcrumbs" />
    <div v-else class="pb-2" />

    <div class="product-info-container d-flex justify-space-between">
      <div class="product-content-container">
        <v-sheet class="product-content__block--album !wd-pb-4" elevation="0" rounded>
          <ProductAlbumSwiper :images="product.images" :is-mobile="isMobile" />
          <div v-if="!isMdAndUp" class="album-info wd-block legacylg:wd-hidden">
            <p class="branch-name mb-2" @click="goToBranch">{{ product.branch_name }}</p>

            <div class="product-name">{{ product.product_name }}</div>

            <p v-if="product.distance" class="distance-text mt-1 mb-0">
              {{ $t('distance_value', { value: product.distance }) }}
            </p>

            <div v-if="hasPenaltyRule">
              <v-divider class="my-5" />
              <ProductPenaltyHint :penalty-rule="productPenaltyRule" />
            </div>
          </div>
        </v-sheet>

        <div v-if="shouldShowAfteePromoHint && !isUsingGift" class="wd-mb-2 xs:(wd-mb-6)">
          <AfteePromoHint />
        </div>

        <!-- 亮點 -->
        <div
          v-if="isEliteProduct"
          class="wd-flex wd-text-pink40 wd-bg-black50 wd-items-center wd-pl-5"
          :class="isMobile ? 'wd-py-3 wd-pr-2' : 'wd-py-2 wd-pr-1 wd-rounded-tl-4px wd-rounded-tr-4px'"
        >
          <v-img class="wd-mr-3" :max-width="72" cover src="@/assets/images/logo/logo_elite.svg?url" />
          <p class="!wd-mb-0 wd-flex wd-flex-auto wd-mr-3 wd-leading-22px">{{ $t('elite_product_detail_bar') }}</p>

          <a
            class="wd-flex wd-items-center wd-px-3 text-white"
            :class="isMobile ? 'wd-h-6 wd-text-osm' : 'wd-h-8'"
            :href="whatsEliteLink"
            target="_blank"
          >
            <p class="!wd-mb-0 wd-leading-22px wd-whitespace-nowrap">
              {{ $t('what_is_elite') }}
            </p>
            <FunnowIcon :class="isMobile ? 'wd-ml-2px' : 'wd-ml-1'" :max-width="isMobile ? '16' : '20'" image-path="chevron-right_white.svg" />
          </a>
        </div>
        <v-sheet
          id="main-product-info"
          class="product-content__block"
          :style="isEliteProduct ? 'border-top-left-radius: unset !important; border-top-right-radius: unset !important;' : ''"
          :rounded="!isMobile"
          elevation="0"
        >
          <product-info-sheet :product="product" />
        </v-sheet>

        <!-- 商品介紹 -->
        <v-sheet v-if="product.productIntro?.length > 0" id="main-product-intro" class="product-content__block" elevation="0" rounded>
          <ProductIntro :intro-list="product.productIntro" />
        </v-sheet>

        <!-- 價目表 -->
        <v-sheet v-if="product.price_list?.length > 0" id="main-product-price-list" class="product-content__block" elevation="0" rounded>
          <ProductPriceList :price-list="product.price_list" :symbol-code="product.symbol_code" />
        </v-sheet>

        <!-- 購買須知 -->
        <v-sheet
          v-if="product.productShoppingNotices?.length > 0"
          id="main-product-shopping-notice"
          class="product-content__block"
          elevation="0"
          rounded
        >
          <ProductShoppingNotice :shopping-notices="product.productShoppingNotices" />
        </v-sheet>

        <!-- 評論（含 FunNow AI） -->
        <v-sheet v-if="comments.total_count > 0" id="main-product-comments" class="product-content__block px-0" elevation="0" rounded>
          <product-comment-sheet
            ref="productCommentSheet"
            :comments="comments"
            :branch="branch"
            :product="product"
            :is-md-and-up="isMdAndUp"
            :funnow-ai-summary="funnowAiSummary"
          />
        </v-sheet>

        <!-- 店家特色 -->
        <div id="main-branch-intro" class="product-content__block product-content__block--no-padding">
          <v-sheet class="wd-px-5 wd-pt-4 wd-pb-5" elevation="0" rounded>
            <branch-intro-sheet
              :product="product"
              :is-mobile="isMobile"
              :google-ai-summaries="googleAiSummaries"
              :google-ai-highlights="googleAiHighlights"
            />
          </v-sheet>
        </div>

        <!-- 店家位置 -->
        <v-sheet id="main-branch-position" class="product-content__block" elevation="0" rounded>
          <product-position-sheet :branch="branch" :product="product" />
        </v-sheet>

        <!-- 注意事項 & 客戶須知 -->
        <v-sheet id="main-product-notices" class="product-content__block product-content__block--notice mb-0" elevation="0" rounded>
          <product-notices-sheet :product="product" />
        </v-sheet>
      </div>

      <div v-if="isMdAndUp" class="booking-card__web">
        <BookingProductCard
          :product="product"
          :comments="branchComments"
          :onboard="onboard"
          :is-disabled-by-elite="isDisabledByElite"
          :show-price="!isUsingGift && !isBookSeatOnly"
          :is-book-seat-only="isBookSeatOnly"
          :is-grouping="!!groupOrderStatus"
          :is-gift="isUsingGift"
        />
      </div>
    </div>

    <!-- 推薦區塊 -->
    <v-sheet v-if="showRecommendSheet" class="recommend-product__container" elevation="0" rounded>
      <div v-if="showSimilarCategory && product.tags?.length > 0" class="similar-categories-tags sub-content__block">
        <p class="sheet-title mb-5">{{ $t('similar_category') }}</p>

        <div class="d-flex">
          <v-btn
            v-for="(tag, idx) in product.tags"
            :key="idx"
            class="tag-item mr-3"
            height="32"
            color="black50"
            variant="outlined"
            @click="goToThemesPage(tag)"
          >
            {{ tag.name }}
          </v-btn>
        </div>
        <v-divider class="mt-5 mb-7" />
      </div>

      <div v-if="otherProducts?.length > 0" class="sub-content__block">
        <product-swiper :title="$t('merchant_other_product')" :items="otherProducts" :source="GtmSource.PRODUCT_DETAIL_SAME_BRANCH" />
      </div>

      <div v-if="showRecommendedProducts" class="sub-content__block">
        <product-swiper :title="$t('recommend_title')" :items="recommendedProducts" :source="GtmSource.PRODUCT_DETAIL_OTHER_SIMILAR" />
      </div>
    </v-sheet>

    <v-sheet v-if="isMdAndDown" class="booking__footer-banner d-flex align-center px-5 py-2" height="64">
      <BookingBanner
        :show-price="!isUsingGift && !isBookSeatOnly"
        show-rating
        :is-grouping="!!groupOrderStatus"
        :is-send-gift="showGiftBtn"
        :is-book-seat-only="isBookSeatOnly"
        :onboard="onboard"
        :isDisabledByElite="isDisabledByElite"
        :rating="product.rating"
        :comments="branchComments"
        :price-setting="{ symbolCode: product.symbol_code, discount: product.discount, rrp: product.rrp, srrp: product.rrp }"
        @open-comment-dialog="openCommentDialog"
      />
    </v-sheet>

    <json-ld-product :product="product" :comments="comments" />
    <json-ld-breadcrumb :all-regions="allRegions" :region="region" :branch="branch" :product="product" />

    <!-- 導下載視窗 -->
    <guide-download-app-modal />
  </v-container>
</template>

<script>
import '@/assets/sass/pages/product-info.scss'
import { mapGetters } from 'vuex'

import tools from '@/mixin/tools'
import giftComposition from '@/composition/gift'
import notificationComposition from '@/composition/notification'
import { usePlatformProductDetail } from '@/composition/useProductInfo'
import { useReferer } from '@/composition/useReferer'
import { useExternalLinks } from '@/composition/useConst'
import { usePageView } from '@/composition/usePageView'

import FunnowIcon from '@/components/icons/funnow-icon.vue'
import ProductAlbumSwiper from '@/components/page/products/ProductAlbumSwiper.vue'
import ProductIntro from '@/components/page/products/ProductIntro.vue'
import ProductInfoSheet from '@/components/page/products/product-info-sheet.vue'
import ProductPriceList from '@/components/page/products/ProductPriceList.vue'
import ProductShoppingNotice from '@/components/page/products/ProductShoppingNotice.vue'
import BranchIntroSheet from '@/components/page/products/branch-intro-sheet.vue'
import ProductPositionSheet from '@/components/page/products/product-position-sheet.vue'
import ProductNoticesSheet from '@/components/page/products/product-notices-sheet.vue'
import JsonLdProduct from '@/components/jsonld/product.vue'
import JsonLdBreadcrumb from '@/components/jsonld/breadcrumb.vue'
import BookingProductCard from '@/components/card/booking-product-card.vue'
import ProductPenaltyHint from '@/components/ProductPenaltyHint.vue'
import AfteePromoHint from '@/components/common/AfteePromoHint.vue'
import { useProductFlags } from '@/composition/useProductFlags'
import utils from '@/utils'
import { ProductSalesMode } from '@/view-models/product'
import { GtmEventMap, GtmSource } from '@/view-models/gtm'
import * as productService from '@/service/product'
import { ASYNC_DATA_KEYS, getAsyncDataKey } from '@/constants/async-data-keys'
import { GOOGLE_BUSINESS_VERTICAL, PaymentMode } from '@/constants/ads'
import { LEGACY_LG_BREAKPOINT } from '@/constants/theme'

export default defineNuxtComponent({
  name: 'ProductInfo',
  components: {
    ContentMenu: defineAsyncComponent(() => import('@/components/navigation/content-menu.vue')),
    Breadcrumbs: defineAsyncComponent(() => import('@/components/breadcrumbs.vue')),
    GuideDownloadAppModal: defineAsyncComponent(() => import('@/components/dialog/guide-download-app-modal.vue')),
    BookingBanner: defineAsyncComponent(() => import('@/components/page/products/booking-banner.vue')),
    FunnowIcon,
    ProductAlbumSwiper,
    ProductIntro,
    ProductInfoSheet,
    ProductPriceList,
    ProductShoppingNotice,
    ProductCommentSheet: defineAsyncComponent(() => import('@/components/page/products/product-comment-sheet.vue')),
    ProductPositionSheet,
    BranchIntroSheet,
    ProductNoticesSheet,
    ProductSwiper: defineAsyncComponent(() => import('@/components/swiper/product-swiper.vue')),
    JsonLdProduct,
    JsonLdBreadcrumb,
    BookingProductCard,
    ProductPenaltyHint,
    AfteePromoHint,
  },
  mixins: [tools],
  setup() {
    definePageMeta({
      middleware: ['products-middleware'],
    })

    const { onBeforeRouteLeaveFn } = useGroupReservationChecker()
    const nuxtApp = useNuxtApp()
    const i18n = useI18n()
    const { getGiftDetail, pathToProductpage } = giftComposition()
    const { getFlashNotification, getGroupNotification, getDeliveryNotification, getGiftNotification, pushGiftExpiringNotification } =
      notificationComposition()

    const { product } = usePlatformProductDetail()
    useReferer({ pid: product.value.pid })
    const route = useRoute()

    const { productDetailTitle, productDescriptionTitle } = usePlatformProductContext()
    const config = useRuntimeConfig()
    const localePath = useLocalePath()

    useHead(() => {
      const pageTitle = `${product.value.product_name} - ${product.value.branch_name} | ${i18n.t('funnow_title')}`
      const ogImage = utils.CDN + utils.imagePath(product.value.cover_image)
      const canonicalUrl = product.value.bid ? `${config.public.BASE_URL}${localePath('/branches/' + product.value.bid)}` : ''
      return {
        title: pageTitle,
        meta: [
          { hid: 'title', name: 'title', content: pageTitle },
          { hid: 'description', name: 'description', content: utils.removeNewLine(product.value.description) },
          { hid: 'keywords', name: 'keywords', content: product.value.keyword || '' },
          { hid: 'og:title', property: 'og:title', content: pageTitle },
          { hid: 'og:description', property: 'og:description', content: utils.removeNewLine(product.value.description) },
          { hid: 'og:image', property: 'og:image', content: ogImage },
          { name: 'robots', content: 'noindex' },
        ],
        ...(canonicalUrl && {
          link: [{ hid: 'i18n-can', rel: 'canonical', href: canonicalUrl }],
        }),
      }
    })

    const isEliteProduct = computed(() => product.value.is_elite)
    const { whatsEliteLink } = useExternalLinks()

    const { isBookSeatOnly } = useProductFlags(product)

    const { shouldShowAfteePromoHint } = useAfteeCampaignPromoHint({
      isPureBooking: isBookSeatOnly,
      currencyCode: computed(() => product.value?.symbol_code),
    })

    const { data: productPenaltyRule, execute: getProductPenaltyRule } = useAsyncData(
      getAsyncDataKey(ASYNC_DATA_KEYS.PRODUCT_PENALTY, [product.value.pid]),
      () =>
        productService.getProductPenaltyRule(nuxtApp.$axios, product.value.pid, {
          currencyId: product.value?.currency_id,
          paymentCurrencyId: product.value?.currency_id,
        }),
      { server: false, immediate: false, watch: [product.value.pid], default: () => null }
    )

    const { hasRule: hasPenaltyRule } = useProductPenaltyRule(productPenaltyRule)

    const {
      googleAiSummaries,
      googleAiHighlights,
      funnowAiSummary,
      hasGoogleAiSummary,
      hasFunNowAiSummary,
      isReady: isReviewSummaryReady,
    } = useReviewSummary(computed(() => product.value.bid))

    usePageView(
      computed(() => ({
        event: GtmEventMap.PRODUCT_DETAIL_VIEWED,
        payload: {
          hasGoogleAiSummary: hasGoogleAiSummary.value,
          hasFunNowAiSummary: hasFunNowAiSummary.value,
        },
      })),
      isReviewSummaryReady
    )

    onMounted(() => {
      getProductPenaltyRule()
    })

    return {
      isBookSeatOnly,
      shouldShowAfteePromoHint,
      getGiftDetail,
      pathToProductpage,
      getFlashNotification,
      getGroupNotification,
      getDeliveryNotification,
      getGiftNotification,
      pushGiftExpiringNotification,
      product,
      isEliteProduct,
      whatsEliteLink,
      onBeforeRouteLeaveFn,
      productPenaltyRule,
      hasPenaltyRule,
      productDetailTitle,
      productDescriptionTitle,
      GtmSource,
      localePath,
      googleAiSummaries,
      googleAiHighlights,
      funnowAiSummary,
    }
  },
  data() {
    return {
      isDisabledByElite: false,
      showSimilarCategory: false, // TODO: 暫時關閉, 等待搜尋頁再開啟
      showContentMenu: false,
      showRecommendedProducts: false,
      recommendedProducts: [],
      isMdAndUp: false,
      isMdAndDown: false,
      isMobile: false,
      activatedMenu: 'productInfo',
      userPosition: { lat: '', lng: '' },
    }
  },
  computed: {
    loggedInUser() {
      return this.$auth.user
    },
    showRecommendSheet() {
      if (this.isUsingGift || this.groupOrderStatus) {
        return false
      }
      return (this.showSimilarCategory && this.product.tags?.length > 0) || this.otherProducts?.length > 0 || this.showRecommendedProducts
    },
    isUsingGift() {
      return !!(this.$route.query.gift_id && this.$route.query.nickname)
    },
    showGiftBtn() {
      return !this.isUsingGift && !!this.product.giftable
    },
    contentMenu() {
      let res = []
      res = res.concat({
        text: this.productDetailTitle,
        value: 'productInfo',
        targetID: 'main-product-info',
        poissonID: 'main-product-info',
      })
      if (this.product.productIntro?.length > 0) {
        res = res.concat({
          text: this.productDescriptionTitle,
          value: 'productIntro',
          targetID: 'main-product-intro',
          poissonID: 'main-product-intro',
        })
      }
      if (this.product.price_list?.length > 0) {
        res = res.concat({
          text: this.$t('price_list_title'),
          value: 'productPriceList',
          targetID: 'main-product-price-list',
          poissonID: 'main-product-price-list',
        })
      }
      if (this.product.productShoppingNotices?.length > 0) {
        res = res.concat({
          text: this.$t('purchasing_notice_title'),
          value: 'productShoppingNotice',
          targetID: 'main-product-shopping-notice',
          poissonID: 'main-product-shopping-notice',
        })
      }

      return [
        ...res,
        {
          text: this.$t('all_comment'),
          value: 'productComments',
          targetID: 'main-product-comments',
          poissonID: 'main-product-comments',
        },
        {
          text: this.$t('branch_info'),
          value: 'branchIntro',
          targetID: 'main-branch-intro',
          poissonID: 'main-branch-intro',
        },
        {
          text: this.$t('all_address'),
          value: 'branchPosition',
          targetID: 'main-branch-position',
          poissonID: 'main-branch-position',
        },
        {
          text: this.$t('all_notice', { branchname: '' }),
          value: 'productNotices',
          targetID: 'main-product-notices',
          poissonID: 'main-product-notices',
        },
      ]
    },
    breadcrumbs() {
      const breadcrumbs = []
      if (this.branch) {
        breadcrumbs.push({
          title: this.product.branch_name,
          to: this.localePath({ name: 'branches-id', params: { id: this.product.bid } }),
        })
      }
      if (this.branch && this.product) {
        breadcrumbs.push({ title: this.product.product_name })
      }
      return breadcrumbs
    },
    onboard() {
      if (this.isUsingGift && this.giftInfo.is_sender) {
        return false
      }
      return this.product.onboard === 1
    },
    branchComments() {
      return { rating: this.branch.rating, totalCount: this.branch.number_of_comments }
    },
    ...mapGetters({
      user: 'authentication/getUser',
      otherProducts: 'product/otherProducts',
      comments: 'comments/productComments',
      allRegions: 'region/allRegions',
      region: 'region/region',
      branch: 'branch/branch',
      groupOrderStatus: 'groupOrderStatus',
      groupOrder: 'groupOrder',
      giftInfo: 'gift/giftInfo',
    }),
  },
  watch: {
    loggedInUser(newUser, prevUser) {
      if (this.isUsingGift) {
        const giftParmers = {
          productID: this.product.pid,
          giftID: this.$route.query.gift_id,
        }

        if (newUser) {
          this.getGiftDetail(giftParmers)
        } else {
          const successCallback = () => {
            this.getGiftDetail(giftParmers)
          }
          this.$store.dispatch('dialog/setLoginDialog', { state: true, options: { success: successCallback } })
        }
      } else if (newUser && !prevUser) {
        this.pushGiftExpiringNotification(newUser)
        this.isDisabledByElite = this.product.is_elite && newUser.is_elite === false
      }
    },
  },
  mounted() {
    const pid = this.$route.params.id
    this.$store.dispatch('comments/getProductComments', { pid: pid, query: 'size=10&sortby=rating_high' })
    this.$store.dispatch('product/getOtherProducts', { pid: pid })
    this.$store.dispatch('branch/getBranch', this.product.bid)
    this.getLocation()
    this.initContentMenu()
    this.onResize()
    this.sendTrackingCode()
    window.addEventListener('scroll', this.handleScroll, { passive: true })

    if (this.user) {
      this.getRegionRecommendedProducts()
      this.isDisabledByElite = this.product.is_elite && this.user.is_elite === false
    }

    this.setNotification()
  },
  beforeRouteLeave(to, from) {
    return this.onBeforeRouteLeaveFn(to, from)
  },
  beforeUnmount() {
    this.$store.dispatch('notification/resetNotification')
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    getLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(position => {
          this.userPosition.lat = position.coords.latitude
          this.userPosition.lng = position.coords.longitude
          this.getProductInfo()
        })
      }
    },
    onResize() {
      this.isMdAndUp = window.innerWidth >= LEGACY_LG_BREAKPOINT
      this.isMdAndDown = window.innerWidth < LEGACY_LG_BREAKPOINT
      this.isMobile = window.innerWidth < 600
    },
    getProductInfo() {
      const { lat, lng } = this.userPosition
      const queryForProductInfo = { lat: lat, lng: lng }
      if (this.groupOrderStatus) {
        Object.assign(queryForProductInfo, {
          booking_time: this.groupOrder.booking_time,
          group_code: this.groupOrder.group_code,
          count: this.groupOrder.count,
        })
      }
      this.$store.dispatch('product/getProductInfo', { pid: this.product.pid, params: queryForProductInfo })
    },
    initContentMenu() {
      this.setShowContentMenu()
      if (this.showContentMenu) {
        this.contentMenuScrollEvent()
      }
    },
    setShowContentMenu() {
      this.showContentMenu = this.$el?.getBoundingClientRect().top + 509 < 0
    },
    contentMenuScrollEvent(event) {
      const pageBounding = this.$el?.getBoundingClientRect()
      const isPageBottom = pageBounding.bottom < document.documentElement.clientHeight
      if (isPageBottom) {
        this.activatedMenu = this.contentMenu[this.contentMenu?.length - 1].value
      } else {
        let minRangeTarget = {}
        this.setShowContentMenu()
        this.contentMenu.forEach(menu => {
          if (document.getElementById(menu.poissonID)) {
            const elBounding = document.getElementById(menu.poissonID).getBoundingClientRect()
            // 128: header高度 + 導覽列高度
            const isDisappear = elBounding.height + elBounding.top - 128 < 0
            if (!isDisappear && elBounding.top < 150) {
              minRangeTarget = Object.assign({}, menu)
            }
          }
        })
        this.activatedMenu = minRangeTarget.value
      }
    },
    getRegionRecommendedProducts() {
      const regionID = this.product.region_id
      this.$store
        .dispatch('region/getRegionRecommendedProducts', regionID)
        .then(res => {
          const products = res.data
          this.showRecommendedProducts = res.total_count > 0
          this.recommendedProducts = products?.filter(
            product => product.sales_mode !== ProductSalesMode.WineDelivery && product.sales_mode !== ProductSalesMode.Movie
          )
        })
        .catch(err => {
          console.error(err)
        })
    },
    handleScroll(event) {
      this.contentMenuScrollEvent()
    },
    openCommentDialog() {
      this.$refs.productCommentSheet.openCommentModal()
    },
    goToBranch() {
      this.$router.push(this.localePath(`/branches/${this.product.bid}`))
    },
    goToThemesPage(tag) {
      console.log(tag)
    },
    setNotification() {
      const notificationSetting = []
      if (this.product.flash_time_type && this.product.flash_time_type !== 'hidden' && !this.isUsingGift) {
        notificationSetting.push(this.getFlashNotification({ flashTime: this.product.flash_time, timeType: this.product.flash_time_type }))
      }

      if (this.groupOrderStatus) {
        notificationSetting.push(this.getGroupNotification({ bookingTime: this.groupOrder.booking_time, nickname: this.groupOrder.nickname }))
      }

      if (this.product.delivery) {
        notificationSetting.push(this.getDeliveryNotification({ freeAmount: this.product.delivery.delivery_free_amount }))
      }

      if (this.isUsingGift) {
        notificationSetting.push(this.getGiftNotification({ nickname: this.$route.query.nickname }))
      }

      this.$store.dispatch('notification/setNotifications', notificationSetting)

      if (this.loggedInUser && !this.isUsingGift) {
        this.pushGiftExpiringNotification(this.loggedInUser)
      }
    },
    sendTrackingCode() {
      this.$gtm.push({ ecommerce: null })
      this.$gtm.push({
        event: 'view_item',
        value: this.product.discount,
        items: [
          {
            id: this.product.pid,
            google_business_vertical: GOOGLE_BUSINESS_VERTICAL,
          },
        ],
        ecommerce: {
          currencyCode: this.product.symbol_code,
          detail: {
            products: [
              {
                name: this.product.product_name,
                id: this.product.pid,
                price: this.product.discount,
                brand: this.product.branch_name,
                category: this.product.category_id,
                platform: 'FunNow',
                payment_mode: this.product.is_paid_onsite ? PaymentMode.ONSITE : PaymentMode.ONLINE,
              },
            ],
          },
        },
      })
    },
  },
})
</script>
